import React from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Icongraphy,
  Text,
  HStack,
  VStack,
  NewBadge,
  IconButton,
  DotBadge,
} from '@design-system/mobile';
import styled from 'styled-components';
import classNames from 'classnames';

import TrackingService from '@/services/tracking';
import useHamburgerMenu from './hooks/useHamburgerMenu';
import { HEADER_HEIGHT, HEADER_Z_INDEX, MAX_Z_INDEX } from '@/styles';

export interface HamburgerMenuProps {
  isOpen: boolean;
  isLayer?: boolean;
  isHeader?: boolean;
  isIframe?: boolean;
  handleClose?: () => void;
}

/**
 * HamburgerMenu
 * @auto 오재원
 * @param param0
 * @returns
 */
const HamburgerMenu = (props: HamburgerMenuProps) => {
  const { isOpen, handleClose, isHeader, isLayer } = props;
  const {
    CATEGORY_TAB_HEIGHT,
    isMounted,
    leftMenuItem,
    leftMenuScrollRef,
    leftMenuListRef,
    rightMenuItem,
    rightMenuItemRefs,
    rightMenuRef,
    rightMenuMarginBottom,
    containerHeight,
    handleLinkUrl,
    scrolledRightItem,
    isMwebAOS,
    VH_100,
    handleClickLeftMenuItem,
    fixedTop,
    selectedTab,
    handleChangeTab,
    CATEGORY_TAB,
  } = useHamburgerMenu(props);

  const HamburgerMenuContent = (
    <HamburgerMenuWrapper
      width="100%"
      height={VH_100}
      alignment="start"
      backgroundColor="color_chip.white"
      {...TrackingService.tagGTM('drawer')}
    >
      {isHeader && handleClose && <DrawerHeader handleClose={handleClose} />}
      <HStack
        width="100%"
        borderBottom="1px solid"
        borderColor="color_chip.primary"
      >
        {CATEGORY_TAB.map((item) => {
          return (
            <VStack
              key={`hamburger-menu-tab-${item.value}`}
              width="50%"
              height={CATEGORY_TAB_HEIGHT}
              px="8px"
              py={18}
              alignment="center"
              backgroundColor={
                item.value === selectedTab
                  ? 'color_chip.primary'
                  : 'color_chip.white'
              }
              onClick={() => {
                handleChangeTab(item.value);
              }}
              {...TrackingService.tagGTM('hamburger_menu_tab_button', {
                'tab-value': item.value,
              })}
            >
              <Text
                variant="label_xlg_sb"
                color={
                  item.value === selectedTab
                    ? 'color_chip.white'
                    : 'color_chip.primary'
                }
              >
                {item.tabName}
              </Text>
            </VStack>
          );
        })}
      </HStack>
      <Box>
        {containerHeight && fixedTop && (
          <Container
            width="100%"
            height={containerHeight}
            position="fixed"
            top={fixedTop}
            zIndex={HEADER_Z_INDEX}
          >
            <VStack width="100%">
              <HStack width="100%" alignment="start">
                {/* left menu section */}
                <LeftMenu
                  width="140px"
                  overflowY="scroll"
                  overflowX="hidden"
                  ref={leftMenuScrollRef}
                  backgroundColor="color_chip.gray.50"
                  height={containerHeight}
                >
                  {leftMenuItem?.map(
                    (
                      { leftMenuName, leftMenuNo, isHighLight, isCategory },
                      index,
                    ) => (
                      <HStack
                        key={`hamburger-left-${index}`}
                        alignment={isCategory === 'N' ? 'start' : 'center'}
                        width="100%"
                        padding="20px 16px"
                        space={2}
                        backgroundColor={
                          scrolledRightItem === index ? 'color_chip.white' : ''
                        }
                        mb={index === leftMenuItem.length - 1 ? '55px' : 0}
                        onClick={() => handleClickLeftMenuItem(index)}
                        ref={(element) => {
                          leftMenuListRef.current[index] = element;
                        }}
                        {...TrackingService.tagGTM('drawer_left_menu_button', {
                          'category-id': leftMenuNo,
                          'category-name': leftMenuName,
                        })}
                      >
                        {/* 쌤페스타 한정 강조 색상 노출 하드코딩 요청 건 */}
                        {leftMenuNo === '20511' ||
                        leftMenuNo === '20512' ||
                        leftMenuName === '쌤페스타' ? (
                          <Text
                            variant="label_xlg_b"
                            color="#FC8300"
                            maxWidth="108px"
                            position="relative"
                          >
                            {leftMenuName}
                          </Text>
                        ) : (
                          <Text
                            variant={
                              scrolledRightItem === index ||
                              (isHighLight === 'Y' && isCategory === 'N')
                                ? 'label_xlg_sb'
                                : 'label_xlg_r'
                            }
                            color="color_chip.primary"
                            maxWidth="108px"
                            position="relative"
                          >
                            {leftMenuName}
                          </Text>
                        )}
                        {leftMenuName === '쌤페스타' && (
                          <NewBadge
                            variant="style1"
                            verticalAlign={isMwebAOS ? 'bottom' : 'top'}
                          />
                        )}
                        {leftMenuName !== '쌤페스타' && isHighLight === 'Y' && (
                          <>
                            {isCategory === 'Y' ? (
                              <NewBadge
                                variant="style1"
                                verticalAlign={isMwebAOS ? 'bottom' : 'top'}
                              />
                            ) : (
                              <DotBadge size="sm" />
                            )}
                          </>
                        )}
                      </HStack>
                    ),
                  )}
                </LeftMenu>
                {/*  right menu section */}
                <RightMenu
                  width={`calc(100% - 140px)`}
                  padding="16px 16px 0px 20px"
                  overflowY="scroll"
                  ref={rightMenuRef}
                  height={containerHeight}
                >
                  <VStack width="100%" space={16}>
                    {rightMenuItem?.map(
                      (
                        {
                          rightMenuName,
                          rightMenuNo,
                          rightMenuList,
                          displayType,
                          linkUrl,
                        },
                        index,
                      ) => (
                        <Box
                          width="100%"
                          key={`hamburger-right-${index}`}
                          ref={(element) => {
                            rightMenuItemRefs.current[index] = element;
                          }}
                          mb={
                            index === rightMenuItem.length - 1 &&
                            rightMenuMarginBottom
                              ? rightMenuMarginBottom
                              : 0
                          }
                        >
                          <HStack
                            space={8}
                            alignment="center"
                            pt="17.5px"
                            pb="17.5px"
                            borderBottom="1px solid"
                            className="hamburger-right"
                            data-seq={index}
                            onClick={() => handleLinkUrl(linkUrl)}
                            {...TrackingService.tagGTM('ndepth_menus_button', {
                              'category-id': rightMenuNo,
                              'category-name': rightMenuName,
                            })}
                          >
                            <Text variant="headline5" width="calc(100% - 20px)">
                              {rightMenuName}
                            </Text>
                            <Icongraphy variant="ic_arrow_right" size={20} />
                          </HStack>
                          {/*
              어드민에서 대카테고리만 보여주는 경우 displayType = I
              어드민에서 대카테고리 + 리스트를 보여주도록 하는 경우 displayType = T
            */}
                          {displayType === 'T' && (
                            <Box mt="16px">
                              {rightMenuList?.map(
                                ({
                                  categoryName,
                                  categoryNo,
                                  isHighLight,
                                  linkUrl,
                                }) => (
                                  <HStack
                                    key={`hamburger-small-${categoryNo}`}
                                    space={2}
                                    py={16}
                                    onClick={() => handleLinkUrl(linkUrl)}
                                    {...TrackingService.tagGTM(
                                      'ndepth_menus_button',
                                      {
                                        'category-id': categoryNo,
                                        'category-name': categoryName,
                                      },
                                    )}
                                  >
                                    <Text variant="label_xlg_r" maxWidth="100%">
                                      {categoryName}
                                    </Text>
                                    {isHighLight === 'Y' && (
                                      <NewBadge
                                        variant="style1"
                                        verticalAlign={
                                          isMwebAOS ? 'bottom' : 'top'
                                        }
                                      />
                                    )}
                                  </HStack>
                                ),
                              )}
                            </Box>
                          )}
                        </Box>
                      ),
                    )}
                  </VStack>
                </RightMenu>
              </HStack>
            </VStack>
          </Container>
        )}
      </Box>
    </HamburgerMenuWrapper>
  );

  return (
    <>
      {isMounted &&
        (isLayer ? (
          createPortal(
            <Layer
              className={classNames({
                open: isOpen,
              })}
              backgroundColor={'color_chip.white'}
              height={VH_100}
            >
              {HamburgerMenuContent}
            </Layer>,
            document.body,
          )
        ) : (
          <>{HamburgerMenuContent}</>
        ))}
    </>
  );
};

export default HamburgerMenu;

interface DrawerHeaderProps {
  handleClose: () => void;
}

const DrawerHeader = ({ handleClose }: DrawerHeaderProps) => {
  return (
    <DrawerHeaderWrapper>
      <DrawerHeaderContainer
        space="end"
        backgroundColor="color_chip.white"
        zIndex={MAX_Z_INDEX}
      >
        <IconButton
          size="lg"
          variant="ic_close"
          onClick={() => handleClose()}
          {...TrackingService.tagGTM('drawer_close_button')}
        />
      </DrawerHeaderContainer>
    </DrawerHeaderWrapper>
  );
};

const HamburgerMenuWrapper = styled(VStack)``;

const DrawerHeaderWrapper = styled.header`
  width: 100%;
  height: ${HEADER_HEIGHT}px;
`;

const DrawerHeaderContainer = styled(HStack)`
  height: ${HEADER_HEIGHT}px;
  position: fixed;
  width: 100%;
  padding: 4px 8px;
  top: 0;
  left: 0;
`;

const Layer = styled(Box)`
  z-index: 999;
  position: fixed;
  overflow-x: hidden;
  overflow-y: auto;
  transition: all 0.3s ease-out;
  // height: 100dvh;
  transform: scaleZ(0);
  top: 0;
  left: -100%;
  bottom: 0;
  width: 100%;

  &.open {
    transform: scaleZ(1);
    top: 0;
    left: 0;
    bottom: 0;
  }
`;

const Container = styled(Box)`
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: none;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
`;

const RightMenu = styled(Box)`
  overscroll-behavior: contain;
`;

const LeftMenu = styled(Box)`
  overscroll-behavior: contain;
`;
